import { call, cancel, fork, put, select } from 'redux-saga/effects';

import { getSavedFilingPeriodByIdRequest } from '@actions/savedFilingPeriod';
import { getStandardTemplatesRequest } from '@actions/standardTemplate';
import { checkStandardTemplateAggregateJobsRunning } from '@api/standardTemplate';
import { requestAggregatesSaga } from '@sagas/filing/aggregates/requestAggregatesSaga';
import {
  processingAggregatesStop,
  processingAggregatesSuccess,
} from '@store/filing/aggregates/aggregates.reducer';
import { aggregatesSelector } from '@store/selectors/filing/aggregates';
import { selectedFilingPeriodSelector } from '@store/selectors/savedFilingPeriod';
import { selectedStandardTemplateSelector } from '@store/selectors/standardTemplate';

// TODO: This can be simplified if we isolate the aggregates functionality. At the moment the processing state for aggregates is controlled from standard template
export const processingAggregatesRequestSaga = function* ({ payload }) {
  try {
    const { processingAggregates, isDataProcessingAggregate } = yield call(
      checkStandardTemplateAggregateJobsRunning,
      payload
    );

    const selectedStandardTemplate = yield select(selectedStandardTemplateSelector);
    const currentAggregates = yield select(aggregatesSelector);
    const currentlyProcessing = currentAggregates.isDataProcessingAggregate;
    const currentStandardTemplateId = selectedStandardTemplate.id;

    /*
     * Because we are polling it is possible that the user is on a different standard template thant the one that kicked off polling.
     * If that is the case we abort and wait for the next poll.
     */
    if (currentStandardTemplateId !== payload) yield cancel();

    /* Terminate early if no aggregates were previously processing and no aggregates are currently loading */
    if (!currentlyProcessing && !isDataProcessingAggregate) yield cancel();

    let shouldUpdateAggregate = false;

    /*
     * If we have processing aggregates, then run through the current aggregates and update.
     */
    if (isDataProcessingAggregate) {
      const results = currentAggregates.sections.map(section => ({
        ...section,
        rows: section?.rows?.map(row => {
          const isProcessing = processingAggregates.includes(row.id);
          if (!isProcessing && row.isProcessing) shouldUpdateAggregate = true;

          return {
            ...row,
            isProcessing,
          };
        }),
      }));

      /*
       * If we only have aggregates entering loading states we can just update the aggregates with the new state
       */
      if (!shouldUpdateAggregate) {
        yield put(
          processingAggregatesSuccess({
            ...currentAggregates,
            sections: results,
            isDataProcessingAggregate,
          })
        );
      }
    }

    /*
     * If any aggregates that were in a loading state come out or if we were processing and now no rows are processing,
     * we have to reload aggregates to get the values after processing
     */
    if (!isDataProcessingAggregate || shouldUpdateAggregate) {
      yield put(processingAggregatesStop());
      yield fork(requestAggregatesSaga, { payload: { id: payload, startPolling: true } });
    }

    /*
     * If we were busy processing, but all aggregates are now done, we have to reload the standardTemplate and savedFilingPeriod to get a new status
     */
    if (!isDataProcessingAggregate && currentlyProcessing) {
      const selectedFilingPeriod = yield select(selectedFilingPeriodSelector);
      yield put(getStandardTemplatesRequest({ savedFilingPeriod: selectedFilingPeriod.id }));
      yield put(getSavedFilingPeriodByIdRequest(selectedFilingPeriod.id));
    }
  } catch (error) {
    // Do nothing
  }
};
